import React, { useCallback, useLayoutEffect, useRef, useState } from 'react'
import { differenceInHours } from 'date-fns'
import {
  MessageRow as MessageRowData,
  RoomHeader,
  UserChip,
} from '../../../generated/api/chat-room'
import { useAppTranslation } from '../../../hooks/useAppTranslation'
import { useModal } from '../../../hooks/useModal'
import { theme } from '../../../theme/theme'
import { InfiniteScroll } from '../../common/InfiniteScroll'
import { SpinnerWrapper } from '../../common/SpinnerWrapper'
import { ChipAvatar } from './ChipAvatar'
import { MessageRow } from './MessageRow'
import { ReadByModal } from './ReadByModal'

const EMPTY_STATE_AVATARS = 5
const NO_READERS: UserChip[] = []
const EMPTY_STATE_NAMES = EMPTY_STATE_AVATARS - 2
const STICK_TO_BOTTOM_THRESHOLD = 100

interface RoomEmptyStateProps {
  header: RoomHeader
}

const RoomEmptyState = ({ header }: RoomEmptyStateProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  if (header.type === 'oneonone' || header.members.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center py-16">
        <p style={theme.textVariants.base} className="text-neutral-50">
          {translate('chatRoom.emptyState.noMessages')}
        </p>
      </div>
    )
  }

  const names = header.members
    .slice(0, EMPTY_STATE_NAMES)
    .map((member) => member.firstName)
    .join(', ')
  const overflow = header.memberCount - EMPTY_STATE_NAMES

  return (
    <div className="flex w-full justify-center pt-8 pb-16">
      <div className="flex flex-col items-center gap-4">
        <p style={theme.textVariants.lead}>{header.title}</p>
        <div className="flex">
          {header.members.slice(0, EMPTY_STATE_AVATARS).map((member, index) => (
            <ChipAvatar
              key={member.userId}
              chip={member}
              size={46}
              className={index !== 0 ? '-ml-2' : ''}
            />
          ))}
        </div>
        <p className="text-neutral-40" style={theme.textVariants.base}>
          {overflow > 0 ? `${names} +${overflow}` : names}
        </p>
      </div>
    </div>
  )
}

interface MessageListProps {
  roomId: string
  header: RoomHeader | undefined
  messages: MessageRowData[]
  readersByMessage: ReadonlyMap<string, UserChip[]>
  currentUserId: string | undefined
  isLoading: boolean
  hasNextPage: boolean
  isFetchingNextPage: boolean
  fetchNextPage: () => void
}

export const MessageList = ({
  roomId,
  header,
  messages,
  readersByMessage,
  currentUserId,
  isLoading,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: MessageListProps): React.ReactNode => {
  const rootRef = useRef<HTMLDivElement | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const scrolledRoomRef = useRef<string | null>(null)
  const isAtBottomRef = useRef(true)
  const [readByMessageId, setReadByMessageId] = useState<string | null>(null)
  const { show, open, close } = useModal()

  const newestId = messages[0]?.id
  const newestIsOwn = messages[0]?.authorId === currentUserId

  useLayoutEffect(() => {
    const scroller = scrollRef.current
    if (!scroller) return
    const isNewRoom = scrolledRoomRef.current !== roomId
    if (!isNewRoom && !isAtBottomRef.current && !newestIsOwn) return
    scrolledRoomRef.current = roomId
    isAtBottomRef.current = true
    scroller.scrollTop = 0
  }, [isLoading, roomId, newestId, newestIsOwn])

  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    isAtBottomRef.current =
      event.currentTarget.scrollTop > -STICK_TO_BOTTOM_THRESHOLD
  }, [])

  const onEndReached = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const onReadByClick = useCallback(
    (messageId: string) => {
      setReadByMessageId(messageId)
      open()
    },
    [open]
  )

  const isGroupChat = header ? header.type !== 'oneonone' : false

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <div ref={rootRef} className="flex h-full bg-neutral-98">
          {isLoading ? (
            <SpinnerWrapper />
          ) : (
            <InfiniteScroll
              key={`room-messages-${roomId}`}
              className="flex flex-col-reverse items-start bg-neutral-98 pl-10 pr-4 pt-8 md:pl-12 md:pr-6"
              overflow="overflow-y-scroll"
              onEndReached={onEndReached}
              isFetching={isFetchingNextPage}
              rootRef={rootRef}
              scrollRef={scrollRef}
              onScroll={onScroll}
            >
              {messages.map((row, index) => {
                const older = messages[index + 1]
                const startsCluster = older
                  ? older.authorId !== row.authorId
                  : true
                const showTimestamp = older
                  ? Math.abs(
                      differenceInHours(row.createdAt, older.createdAt)
                    ) > 0
                  : true
                return (
                  <MessageRow
                    key={row.id}
                    row={row}
                    readers={readersByMessage.get(row.id) ?? NO_READERS}
                    isOwn={row.authorId === currentUserId}
                    isGroupChat={isGroupChat}
                    isNewest={index === 0}
                    startsCluster={startsCluster}
                    showTimestamp={showTimestamp}
                    onReadByClick={onReadByClick}
                  />
                )
              })}
              {header && messages.length === 0 && (
                <RoomEmptyState header={header} />
              )}
            </InfiniteScroll>
          )}
        </div>
      </div>
      {show && readByMessageId && (
        <ReadByModal
          show={show}
          close={close}
          roomId={roomId}
          messageId={readByMessageId}
        />
      )}
    </>
  )
}
